// ============================================
// Format helpers — money, dates, phones, addresses
// ============================================

/**
 * Format a number as USD currency.
 */
export function formatMoney(amount) {
  if (amount == null || isNaN(amount)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

/**
 * Format a US phone number for display: (516) 386-9923
 * Accepts E.164 (+15163869923), 10-digit, or already-formatted input.
 */
export function formatPhone(phone) {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (ten.length !== 10) return phone; // fallback
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

/**
 * Normalize phone to E.164 for storage: +15163869923
 */
export function normalizePhone(phone) {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return phone; // unknown format, return as-is
}

/**
 * Format a Firestore timestamp or Date as a short date: "Jan 5, 2026"
 */
export function formatDate(input) {
  if (!input) return '';
  const date = input.toDate ? input.toDate() : new Date(input);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a Firestore timestamp or Date as date + time: "Jan 5, 2026 at 2:30 PM"
 */
export function formatDateTime(input) {
  if (!input) return '';
  const date = input.toDate ? input.toDate() : new Date(input);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Time-only format: "2:30 PM"
 */
export function formatTime(input) {
  if (!input) return '';
  const date = input.toDate ? input.toDate() : new Date(input);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Format a single-line address from a property address object.
 */
export function formatAddressLine(address) {
  if (!address) return '';
  if (address.formatted) return address.formatted;
  const parts = [
    address.street,
    address.unit ? `, ${address.unit}` : '',
    address.street ? ', ' : '',
    address.city,
    address.state ? `, ${address.state}` : '',
    address.zip ? ` ${address.zip}` : '',
  ];
  return parts.join('').trim();
}

/**
 * Generate a short ID — used for tokens, job numbers, etc.
 * Not cryptographically secure; for true tokens use crypto.randomUUID() server-side.
 */
export function shortId(length = 8) {
  return Math.random().toString(36).slice(2, 2 + length);
}

/**
 * Pad a sequence number for display: 1 -> "0001"
 */
export function padNumber(n, width = 4) {
  return String(n).padStart(width, '0');
}
